import axios, { AxiosError, AxiosResponse, AxiosStatic } from 'axios';
import { all, call, put, takeLatest } from '@redux-saga/core/effects';

import { SNACK_CRITICAL, SNACK_SUCCESS } from '@neslotech/utils';

import {
  getAllMandatesRequest,
  getUpdateMandateV3Request,
  getCreateMandateV3Request,
  getDeleteMandateV3Request,
  getMandateVersionsRequest,
  getMandateDocumentsRequest,
  getAttachMandateDocumentRequest,
  getDetachMandateDocumentRequest,
  RequestOptions
} from '../tools/api';

import {
  LoadMandatesAction,
  LoadMandateAction,
  UpdateMandateAction
} from '../actions/mandate/mandate.types';
import { MandateActions } from '../actions/mandate/mandate.actions';
import { SystemActions } from '../actions/system/system.actions';

// Local action shapes for the v3 document attach / detach flows.
interface AttachMandateDocumentAction {
  type: string;
  id: number | string;
  payload: { name: string; data: string };
  onComplete: () => void;
}
interface DetachMandateDocumentAction {
  type: string;
  id: number | string;
  attachmentId: number | string;
  onComplete: () => void;
}

export function* performLoadMandates({ onComplete }: LoadMandatesAction) {
  try {
    const [endpoint, requestOptions]: RequestOptions = getAllMandatesRequest();
    const { data } = yield call<AxiosStatic>(axios, endpoint, requestOptions);
    yield put({ type: MandateActions.SET_MANDATES, mandates: data });
  } catch (error) {
    if (!(error instanceof AxiosError)) throw error;
    const message = error.response?.data?.message;
    yield put(SystemActions.addNotice(message, SNACK_CRITICAL));
  } finally {
    yield call(onComplete);
  }
}

export function* watchForLoadMandatesRequest() {
  yield takeLatest(MandateActions.LOAD_MANDATES, performLoadMandates);
}

/**
 * v3 in-place update.  PUT /mandates/:id then refresh the list so any other
 * page already showing the mandate (Dashboard, ClientDetail, MandatesList)
 * picks up the new stage/progress/lead.
 */
export function* performUpdateMandate({ id, payload, onComplete }: UpdateMandateAction) {
  try {
    const [endpoint, requestOptions]: RequestOptions = getUpdateMandateV3Request(id, payload);
    const response: AxiosResponse = yield call<AxiosStatic>(axios, endpoint, requestOptions);

    // Refresh the full list (cheap — 7 mandates) so all v3 surfaces re-render
    // with the new field values.
    const [listEndpoint, listOptions]: RequestOptions = getAllMandatesRequest();
    const list: AxiosResponse = yield call<AxiosStatic>(axios, listEndpoint, listOptions);
    yield put({ type: MandateActions.SET_MANDATES, mandates: list.data });
    yield put({ type: MandateActions.SET_MANDATE, mandate: response.data });

    yield put(SystemActions.addNotice('Mandate updated.', SNACK_SUCCESS));
  } catch (error) {
    if (!(error instanceof AxiosError)) throw error;
    const message =
      error.response?.data?.message ||
      (typeof error.response?.data === 'object'
        ? Object.entries(error.response.data)
            .map(([k, v]) => `${k} ${Array.isArray(v) ? v.join(', ') : v}`)
            .join('; ')
        : 'Unable to update mandate.');
    yield put(SystemActions.addNotice(message, SNACK_CRITICAL));
  } finally {
    yield call(onComplete);
  }
}

export function* watchForUpdateMandateRequest() {
  yield takeLatest(MandateActions.UPDATE_MANDATE, performUpdateMandate);
}

/**
 * v3 top-level create.  POST /mandates → refresh list → invoke caller's
 * onComplete with the created record so it can navigate to the detail page.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* performCreateMandate({ payload, onComplete }: any) {
  let created: any = undefined;
  try {
    const [endpoint, requestOptions]: RequestOptions = getCreateMandateV3Request(payload);
    const response: AxiosResponse = yield call<AxiosStatic>(axios, endpoint, requestOptions);
    created = response.data;

    // Pull the fresh list so dashboard / mandates list reflect the new entry.
    const [listEndpoint, listOptions]: RequestOptions = getAllMandatesRequest();
    const list: AxiosResponse = yield call<AxiosStatic>(axios, listEndpoint, listOptions);
    yield put({ type: MandateActions.SET_MANDATES, mandates: list.data });
    yield put({ type: MandateActions.SET_MANDATE, mandate: created });

    yield put(SystemActions.addNotice('Mandate created.', SNACK_SUCCESS));
  } catch (error) {
    if (!(error instanceof AxiosError)) throw error;
    const message =
      error.response?.data?.message ||
      (typeof error.response?.data === 'object'
        ? Object.entries(error.response.data)
            .map(([k, v]) => `${k} ${Array.isArray(v) ? v.join(', ') : v}`)
            .join('; ')
        : 'Unable to create mandate.');
    yield put(SystemActions.addNotice(message, SNACK_CRITICAL));
  } finally {
    yield call(onComplete, created);
  }
}

/**
 * v3 — delete a mandate outright.  This is a hard delete on the API side and
 * cascades to the mandate's attached documents, so the UI confirms first.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* performDeleteMandate({ id, onComplete }: any) {
  let ok = false;
  try {
    const [endpoint, requestOptions]: RequestOptions = getDeleteMandateV3Request(id);
    yield call<AxiosStatic>(axios, endpoint, requestOptions);
    ok = true;

    // Refresh the list so the row disappears everywhere it is shown.
    const [listEndpoint, listOptions]: RequestOptions = getAllMandatesRequest();
    const list: AxiosResponse = yield call<AxiosStatic>(axios, listEndpoint, listOptions);
    yield put({ type: MandateActions.SET_MANDATES, mandates: list.data });

    yield put(SystemActions.addNotice('Mandate deleted.', SNACK_SUCCESS));
  } catch (error) {
    if (!(error instanceof AxiosError)) throw error;
    const message =
      error.response?.status === 403
        ? 'You are not authorized to delete mandates.'
        : error.response?.data?.message || 'Unable to delete mandate.';
    yield put(SystemActions.addNotice(message, SNACK_CRITICAL));
  } finally {
    yield call(onComplete, ok);
  }
}

export function* watchForDeleteMandateRequest() {
  yield takeLatest(MandateActions.DELETE_MANDATE, performDeleteMandate);
}

export function* watchForCreateMandateRequest() {
  yield takeLatest(MandateActions.CREATE_MANDATE, performCreateMandate);
}

/**
 * v3 — fetch paper_trail version history for a single mandate.  The result
 * is keyed by id under mandate_store.versionsByMandate so subsequent
 * Activity-tab visits reuse the cached array until the next refresh /
 * explicit reload.
 */
export function* performLoadMandateVersions({ id, onComplete }: LoadMandateAction) {
  try {
    const [endpoint, requestOptions]: RequestOptions = getMandateVersionsRequest(id);
    const { data } = yield call<AxiosStatic>(axios, endpoint, requestOptions);
    yield put({ type: MandateActions.SET_MANDATE_VERSIONS, id, versions: data });
  } catch (error) {
    if (!(error instanceof AxiosError)) throw error;
    const message = error.response?.data?.message || 'Could not load version history.';
    yield put(SystemActions.addNotice(message, SNACK_CRITICAL));
  } finally {
    yield call(onComplete);
  }
}

export function* watchForLoadMandateVersionsRequest() {
  yield takeLatest(MandateActions.LOAD_MANDATE_VERSIONS, performLoadMandateVersions);
}

/* ---- Per-mandate document attachments -------------------------------- */

export function* performLoadMandateDocuments({ id, onComplete }: LoadMandateAction) {
  try {
    const [endpoint, requestOptions]: RequestOptions = getMandateDocumentsRequest(id);
    const { data } = yield call<AxiosStatic>(axios, endpoint, requestOptions);
    yield put({ type: MandateActions.SET_MANDATE_DOCUMENTS, id, documents: data });
  } catch (error) {
    if (!(error instanceof AxiosError)) throw error;
    const message = error.response?.data?.message || 'Could not load mandate documents.';
    yield put(SystemActions.addNotice(message, SNACK_CRITICAL));
  } finally {
    yield call(onComplete);
  }
}

export function* watchForLoadMandateDocumentsRequest() {
  yield takeLatest(MandateActions.LOAD_MANDATE_DOCUMENTS, performLoadMandateDocuments);
}

export function* performAttachMandateDocument({
  id,
  payload,
  onComplete
}: AttachMandateDocumentAction) {
  try {
    const [endpoint, requestOptions]: RequestOptions = getAttachMandateDocumentRequest(id, payload);
    const { data } = yield call<AxiosStatic>(axios, endpoint, requestOptions);
    // Server returns the refreshed full attachment list — write it
    // straight into the store so the Documents tab reflects the new
    // file without a follow-up GET.
    yield put({ type: MandateActions.SET_MANDATE_DOCUMENTS, id, documents: data });
    yield put(SystemActions.addNotice('Document uploaded.', SNACK_SUCCESS));
  } catch (error) {
    if (!(error instanceof AxiosError)) throw error;
    const message = error.response?.data?.message || 'Could not upload document.';
    yield put(SystemActions.addNotice(message, SNACK_CRITICAL));
  } finally {
    yield call(onComplete);
  }
}

export function* watchForAttachMandateDocumentRequest() {
  yield takeLatest(MandateActions.ATTACH_MANDATE_DOCUMENT, performAttachMandateDocument);
}

export function* performDetachMandateDocument({
  id,
  attachmentId,
  onComplete
}: DetachMandateDocumentAction) {
  try {
    const [endpoint, requestOptions]: RequestOptions = getDetachMandateDocumentRequest(
      id,
      attachmentId
    );
    yield call<AxiosStatic>(axios, endpoint, requestOptions);
    // After purge, refresh the list so the row disappears.
    const [listEndpoint, listOptions]: RequestOptions = getMandateDocumentsRequest(id);
    const { data } = yield call<AxiosStatic>(axios, listEndpoint, listOptions);
    yield put({ type: MandateActions.SET_MANDATE_DOCUMENTS, id, documents: data });
    yield put(SystemActions.addNotice('Document removed.', SNACK_SUCCESS));
  } catch (error) {
    if (!(error instanceof AxiosError)) throw error;
    const message = error.response?.data?.message || 'Could not remove document.';
    yield put(SystemActions.addNotice(message, SNACK_CRITICAL));
  } finally {
    yield call(onComplete);
  }
}

export function* watchForDetachMandateDocumentRequest() {
  yield takeLatest(MandateActions.DETACH_MANDATE_DOCUMENT, performDetachMandateDocument);
}

export default function* mandateSaga() {
  yield all([
    watchForLoadMandatesRequest(),
    watchForUpdateMandateRequest(),
    watchForCreateMandateRequest(),
    watchForDeleteMandateRequest(),
    watchForLoadMandateVersionsRequest(),
    watchForLoadMandateDocumentsRequest(),
    watchForAttachMandateDocumentRequest(),
    watchForDetachMandateDocumentRequest()
  ]);
}
